import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from 'components/Button';
import DropdownFilter from 'components/DropdownFilter';
import Map from 'components/Map';
import Icon from 'components/Icon';
import Input from 'components/Input';
import ProjectMarkers from 'components/ProjectMarkers';
import RightNowBox from 'components/RightNowBox';
import ProjectCard from 'components/ProjectCard';
import FilterToggleButton from 'components/FilterToggleButton';

import css from './ProjectListPage.module.scss';
import formHelper from 'js/form-helper';
import api from 'js/api-helper';
//import content from 'content/projects/list';

const SEARCH_NAME = "q";

function remapZeroKeyToEmptyString(object) {
    if(object.key === 0) {
        return {
            key: "",
            value: object.value
        };
    }

    return object;
}

class ProjectListPage extends React.Component {
    static propTypes = {
        "apiUrl": PropTypes.string.isRequired,
        "header": PropTypes.any,
        "searchPlaceholder": PropTypes.any,
        "rightNowLabel": PropTypes.any,
        "webcamList": PropTypes.array.isRequired,
        "infographicList": PropTypes.object,
        "projectStatusLabel": PropTypes.string,
        "resetFilterLabel": PropTypes.string.isRequired,
        "mapLabel": PropTypes.string.isRequired,
        "resultsLabel": PropTypes.string.isRequired,
        "filterLabel": PropTypes.string.isRequired,
        "statusLabel": PropTypes.string.isRequired,
        "divisionLabel": PropTypes.string.isRequired,
        "regionsLabel": PropTypes.string.isRequired,
        "projectCategoriesLabel": PropTypes.string.isRequired,
        "projectCategoriesMultiLabel": PropTypes.string.isRequired,
        "companiesMultiLabel": PropTypes.string.isRequired,
        "companiesLabel": PropTypes.string.isRequired,
        "selectedCustomersLabel": PropTypes.string.isRequired,
        "printLabel": PropTypes.string.isRequired,
        "statuses": PropTypes.any,
        "divisions": PropTypes.any,
        "regions": PropTypes.any,
        "projectCategories": PropTypes.any,
        "companies": PropTypes.any,
        "selectedCustomers": PropTypes.any,
        "projects": PropTypes.any,
        "mapCenterLocation": PropTypes.string.isRequired,
        "defaultZoomLevel": PropTypes.number.isRequired,
        "language": PropTypes.string.isRequired,
        "pageSize": PropTypes.number.isRequired,
        "tileLayer": PropTypes.string
    }

    state = {
        query: {},
        filters: this._getDefaultFilter(),
        projects: [],
        secondaryFilters: false,
        project: null,
        view: 'grid',
        search: false,
        searchValue: "",
        mobile: "results",
        searching: false,
    }

    constructor(props) {
        super(props);

        // TODO: Refactor prop structure between all usecases
        // for multi dropdown ({key:0} vs. {key:""})
        this.statuses = props.statuses;
        this.divisions = props.divisions;
        this.regions = props.regions;
        this.projectCategories = props.projectCategories.map(remapZeroKeyToEmptyString);
        this.companies = props.companies.map(remapZeroKeyToEmptyString);
        this.selectedCustomers = props.selectedCustomers;
    }

    buildInitQuery = () => {
        var query = formHelper.createQuery();
        formHelper.fillQueryFromPageQueryString(query);
        return query;
    };

    componentDidMount() {
        // Fix map position issue on mockup
        // Cause: style-loader ?
        if(window.location.hostname === "localhost") {
            window.addEventListener('DOMContentLoaded', () => {
                window.setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                });
            });
        }

        this.setFiltersFromQuery();
    }

    setFiltersFromQuery() {
        const { query } = this.state;
        const { division, status, region, category, company, customer, q } = query;
        const updatedFilters = {};
        const isSearchDefined = q !== undefined;
    
        if (division) updatedFilters['division'] = division;
        if (status) updatedFilters['status'] = status;
        if (region) updatedFilters['region'] = region;
        if (category) updatedFilters['category'] = Array.isArray(category) ? category : [category];
        if (company) updatedFilters['company'] = Array.isArray(company) ? company : [company];
        if (customer) updatedFilters['customer'] = customer;
    
        if (Object.keys(updatedFilters).length > 0) {
            this.setState(prevState => ({
                filters: { ...prevState.filters, ...updatedFilters },
                search: isSearchDefined,
                searchValue: isSearchDefined ? q : prevState.searchValue
            }), this._filterItems);
        } else {
            this.setState({ 
                projects: this.props.projects.items, 
                search: isSearchDefined,
                searchValue: isSearchDefined ? q : ''
            });
        }
    }

    _getDefaultFilter() {
        return {
            category: [],
            company: [],
            language: this.props.language,
            pageSize: this.props.pageSize,
        };
    }

    _handleResetFilter = () => {
        const resetQuery = {};
        const newUrl = window.location.pathname;
        window.history.replaceState(null, null, newUrl);
    
        this.setState({
            filters: this._getDefaultFilter(),
            query: resetQuery,
            projects: this.props.projects.items
        });
    };

    _handleMouseEnter = e => this.setState({
        project: parseInt(e.currentTarget.dataset.projectId)
    });

    _handleMouseLeave = () => this.setState({ project: null });

    _getMarker(project) {
        if(project.id === this.state.project) return "project-hover";

        return project.completed ? "project-done" : "project-active";
    }

    _toggleSecondaryFilters = () => this.setState({
        secondaryFilters: !this.state.secondaryFilters
    })

    _toggleSearch = () => this.setState({
        search: !this.state.search
    }, () => {
        if(this.state.search && this.search) {
            const input = this.search.querySelector('input');
            input.focus();
        }
    })

    _handleViewClick = e => this.setState({
        view: e.currentTarget.dataset.view
    })

    _handleSearchChange = e => {
        e.preventDefault();
        const inputValue = e.target.value;   
        this.setState(
            prevState => ({
                searchValue: inputValue,
                query: { ...prevState.query, [SEARCH_NAME]: inputValue }
            }),
            () => {
                formHelper.updatePageQueryString(this.state.query);
                window.dispatchEvent(new Event("OnScreenTrigger"));
            }
        );
    };

    _handleSearchKeyDown = e => {
        // If ESC is pressed in the search box
        // clear the search field and hide it
        // and set focus back to the search toggle button

        if(e.keyCode === 27) {
            this.setState(
                prevState => ({
                    search: false,
                    searchValue: "",
                    query: { ...prevState.query, [SEARCH_NAME]: '' }
                }),
                () => {
                    formHelper.updatePageQueryString(this.state.query);
                    this.searchToggle.focus();
                }
            );
        }
    }

    _handleLocationClick = e => {
        this.wrapper.dispatchEvent(new CustomEvent('project', {
            "detail": parseInt(e.currentTarget.dataset.project)
        }));
    }

    _handleFilter = (e, value, filter) => {
        this.setState(prevState => {
            const updatedFilters = {
                ...prevState.filters,
                [filter]: value,
            };
    
            if (filter) {
                const query = { ...prevState.query };
                formHelper.setQueryValue(query, filter, value);
                formHelper.updatePageQueryString(query);
                return { filters: updatedFilters, query };
            }
    
            return { filters: updatedFilters };
        }, this._filterItems);
    }

    _prepareFilter = () => {
        const { filters } = this.state;

        const newFilter = {
            ...filters
        };

        Object.keys(newFilter).forEach(key => {
            if (Array.isArray(newFilter[key])) {
                // If "All" is included send an empty array
                if(newFilter[key].includes("")) {
                    newFilter[key] = [];
                } else {
                    newFilter[key] = newFilter[key].map(v => parseInt(v)).filter(v => v > 0);
                }
            }
        });

        return newFilter;
    }

    _filterItems = () => {
        this.setState({
            searching: true,
        });
        api
            .execute(this.props.apiUrl, this._prepareFilter())
            .then(response => {

                if(response && response.items){
                    this.setState({
                        projects: response.items
                    }, () => {
                        window.dispatchEvent(
                            new Event("OnScreenTrigger")
                        );
                    });
                }else{
                    console.error(response);
                }

                this.setState({
                    searching: false,
                });
            })
            .catch(err => {
                console.error(err);

                this.setState({
                    searching: false,
                });
            });
        
    }

    _filterSearch = project => {
        const {
            searchValue
        } = this.state;

        if(searchValue && searchValue.length < 3) return true;

        return project.name.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
    }

    _mapToggle = () => this.setState(state => ({
        mobile: state.mobile === "map" ? "results" : "map"
    }), () => {
        // Fixes map layout on mobile hopefully
        window.dispatchEvent(new Event('resize'));
    })

    _getActualActiveFilters = () => {
        const { state } = this;

        let count = 0;

        for(let filter in state.filters) {
            if(filter === 'language' || filter === 'pageSize') continue;
            if(Array.isArray(state.filters[filter]) && state.filters[filter].length === 0) continue;
            if(state.filters[filter] === 0) continue;
            if(state.filters[filter] === "0") continue;

            count++;
        }

        return count;
    }

    _getPrintUrl = () => {
        const {
            state,
            props,
        } = this;

        const projects = state.projects.filter(this._filterSearch);
        return props.printProjectsUrl + '?ids=' + projects.map(p => p.id).join(',');
    }

    componentWillMount() {
        if (typeof window !== 'undefined') {
            var initQuery = this.buildInitQuery();
            this.setState({ query: initQuery });
        }
    }

    render() {
        const {
            header,
            searchPlaceholder,
            rightNowLabel,
            resetFilterLabel,
            mapLabel,
            resultsLabel,
            webcamList,
            infographicList,
            projectStatusLabel,
            filterLabel,
            statusLabel,
            divisionLabel,
            regionsLabel,
            projectCategoriesLabel,
            projectCategoriesMultiLabel,
            companiesMultiLabel,
            companiesLabel,
            selectedCustomersLabel,
            printLabel,
            mapCenterLocation,
            defaultZoomLevel,
            tileLayer
        } = this.props;

        const {
            state,
        } = this;

        const infographics = infographicList && infographicList.graphics && infographicList.graphics.infographicBlock;
        return (
            <div
                className={css.wrapper}
                id="project-list"
                ref={node => this.wrapper = node}
            >
                <header>
                    <h1>{header}</h1>
                    {(webcamList && webcamList.length > 0 || infographics && infographics.length > 0) &&
                        <RightNowBox
                            className={css.rightNow}
                            header={rightNowLabel}
                            webcams={webcamList}
                            infographics={infographics}
                        />
                    }
                </header>
                <div className={cn(css.filters, css.primaryFilters)}>
                    <ul>
                        {this.statuses && this.statuses.length > 0 && (
                            <li data-filter="status">
                                <DropdownFilter
                                    active={state.filters.status}
                                    heading={statusLabel}
                                    items={this.statuses}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="status"
                                    className="status"
                                />
                            </li>
                        )}
                        {this.divisions && this.divisions.length > 0 && (
                            <li data-filter="division">
                                <DropdownFilter
                                    active={state.filters.division}
                                    heading={divisionLabel}
                                    items={this.divisions}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="division"
                                    className="business-area"
                                />
                            </li>
                        )}
                        {this.regions && this.regions.length > 0 && (
                            <li data-filter="region">
                                <DropdownFilter
                                    active={state.filters.region}
                                    heading={regionsLabel}
                                    items={this.regions}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="region"
                                    className="geography"
                                />
                            </li>
                        )}
                        <li className={css.filterButtonColumn}>
                            <FilterToggleButton
                                activeFilters={this._getActualActiveFilters()}
                                label={filterLabel}
                                onClick={this._toggleSecondaryFilters}
                                open={this.state.secondaryFilters}
                            />
                        </li>
                    </ul>
                </div>
                <div
                    className={cn(css.filters, css.secondaryFilters)}
                    style={{ display: !this.state.secondaryFilters && 'none' }}
                >
                    <ul>
                        {this.divisions && this.divisions.length > 0 && (
                            <li data-filter="division">
                                <DropdownFilter
                                    active={state.filters.division}
                                    heading={divisionLabel}
                                    items={this.divisions}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="division"
                                    className="business-area"
                                />
                            </li>
                        )}
                        {this.regions && this.regions.length > 0 && (
                            <li data-filter="region">
                                <DropdownFilter
                                    active={state.filters.region}
                                    heading={regionsLabel}
                                    items={this.regions}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="region"
                                    className="geography"
                                />
                            </li>
                        )}
                        {this.projectCategories && this.projectCategories.length > 0 && (
                            <li>
                                <DropdownFilter
                                    active={state.filters.category}
                                    heading={projectCategoriesLabel}
                                    items={this.projectCategories}
                                    onSelect={this._handleFilter}
                                    yellow
                                    multi
                                    multiLabel={projectCategoriesMultiLabel}
                                    name="category"
                                    className="project-category"
                                />
                            </li>
                        )}
                        {this.companies && this.companies.length > 0 && (
                            <li>
                                <DropdownFilter
                                    active={state.filters.company}
                                    heading={companiesLabel}
                                    items={this.companies}
                                    onSelect={this._handleFilter}
                                    yellow
                                    multi
                                    multiLabel={companiesMultiLabel}
                                    name="company"
                                    className="unit"
                                />
                            </li>
                        )}
                        {this.selectedCustomers && this.selectedCustomers.length > 0 && (
                            <li>
                                <DropdownFilter
                                    active={state.filters.customer}
                                    heading={selectedCustomersLabel}
                                    items={this.selectedCustomers}
                                    onSelect={this._handleFilter}
                                    yellow
                                    name="customer"
                                    className="customer"
                                />
                            </li>
                        )}
                        <li className={css.resetFilters}>
                            <Button onClick={this._handleResetFilter} yellow className="reset-filter">
                                {resetFilterLabel}
                            </Button>
                        </li>
                        <li className={css.closeFilters}>
                            <button onClick={this._toggleSecondaryFilters}>
                                <Icon icon="close" />
                            </button>
                        </li>
                    </ul>
                </div>
                <div className={cn(css.content, {
                    [css.mapActive]: this.state.mobile === "map",
                    [css.resultsActive]: this.state.mobile === "results",
                })}>
                   
                    <div className={css.projects}>
                        <div>
                            <ul className={css.options}>
                                <li className={css.search} ref={node => this.search = node}>
                                    <button
                                        onClick={this._toggleSearch}
                                        ref={node => this.searchToggle = node}
                                    >
                                        <Icon
                                            className={this.state.search ? css.searchActive : ""}
                                            icon="magnifier"
                                        />
                                    </button>
                                    <Input
                                        className={cn(css.searchInput, {
                                            [css.searchActive]: this.state.search
                                        })}
                                        id="project-search"
                                        onKeyDown={this._handleSearchKeyDown}
                                        onChange={this._handleSearchChange}
                                        placeholder={searchPlaceholder}
                                        style={{ display: !this.state.search && 'none' }}
                                        type="text"
                                        value={this.state.searchValue}
                                        useValueAttr={true}
                                    />
                                </li>
                                <li className={css.display}>
                                    <button
                                        data-view="grid"
                                        onClick={this._handleViewClick}
                                    >
                                        <Icon
                                            className={cn({
                                                [css.active]: this.state.view === "grid"
                                            })}
                                            icon="grid"
                                        />
                                    </button>
                                    <button
                                        data-view="list"
                                        onClick={this._handleViewClick}
                                    >
                                        <Icon
                                            className={cn({
                                                [css.active]: this.state.view === "list"
                                            })}
                                            icon="list"
                                        />
                                    </button>
                                    <a
                                        className={css.print}
                                        href={this._getPrintUrl()}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        <Icon icon="printer" />
                                        <span>{printLabel}</span>
                                    </a>
                                </li>
                            </ul>
                            <ul className={cn(css.list, {
                                [css.grid]: state.view === "grid",
                                [css.searching]: state.searching,
                            })}>
                                {state.projects && state.projects.filter(this._filterSearch).map(project => (
                                    <li key={project.id}>
                                        <ProjectCard
                                            {...project}
                                            projectStatusLabel={projectStatusLabel}
                                            view={this.state.view}
                                            onLocationClick={this._handleLocationClick}
                                            //onMouseEnter={this._handleMouseEnter} TODO: Optimize renders
                                            //onMouseLeave={this._handleMouseLeave} TODO: Optimize renders
                                            displayText={false}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className={css.map}>
                        <Map
                            center={mapCenterLocation && mapCenterLocation.split(",").map(parseFloat)}
                            zoomLevel={defaultZoomLevel}
                            tileLayer={tileLayer}
                        >
                            <ProjectMarkers
                                filterSearch={this._filterSearch}
                                onMarkerClick={this._handleMarkerClick}
                                projects={state.projects}
                                projectStatusLabel={projectStatusLabel}
                                searchValue={this.state.searchValue}
                            />
                        </Map>
                    </div>
                </div>
                <div className={css.mapToggle}>
                    <Button
                        icon={this.state.mobile === "map" ? "grid" : "map"}
                        onClick={this._mapToggle}
                    >
                        {this.state.mobile === "map"
                            ? resultsLabel
                            : mapLabel
                        }
                    </Button>
                </div>
            </div>
        );
    }
}

export default ProjectListPage;



// WEBPACK FOOTER //
// ./src/components/ProjectListPage/ProjectListPage.jsx